import { EnvironmentProviders, InjectionToken, makeEnvironmentProviders } from '@angular/core';
import { PdfFitMode, PdfTheme } from './models/pdf-viewer.model';

/**
 * Application-wide defaults for every `<app-pdf-viewer>`. A component input always wins over the
 * global value, which in turn wins over {@link DEFAULT_PDF_VIEWER_CONFIG}.
 */
export interface PdfViewerConfig {
  /** Initial zoom as a percentage (100 = actual size). */
  defaultZoom: number;
  minZoom: number;
  maxZoom: number;
  /** Multiplicative step applied by the zoom in/out buttons. */
  zoomStep: number;
  fitMode: PdfFitMode;
  theme: PdfTheme;
  showToolbar: boolean;
  showSidebar: boolean;
  showThumbnails: boolean;
  showSearch: boolean;
  showDownload: boolean;
  showPrint: boolean;
  showRotate: boolean;
  showZoom: boolean;
  showFullscreen: boolean;
  /** Show the light/dark theme toggle. Off by default. */
  showTheme: boolean;
  enableKeyboardNavigation: boolean;
  enableContextMenu: boolean;
  /**
   * URL of the PDF.js worker. Defaults to the asset copied by the `angular.json` build glob.
   * Override only when serving the worker from a different path/CDN.
   */
  workerSrc: string;
  /**
   * Number of fully-rendered page canvases kept in memory at once (LRU). Lower it on
   * memory-constrained targets; raise it for smoother fast-scrolling. Must be >= 3.
   */
  maxRenderedPages: number;
  /** Device-pixel-ratio cap used when sizing canvases — guards against huge retina canvases. */
  maxDevicePixelRatio: number;
}

export const DEFAULT_PDF_VIEWER_CONFIG: PdfViewerConfig = {
  defaultZoom: 100,
  minZoom: 50,
  maxZoom: 500,
  zoomStep: 1.25,
  fitMode: 'auto',
  theme: 'light',
  showToolbar: true,
  showSidebar: true,
  showThumbnails: true,
  showSearch: true,
  showDownload: false,
  showPrint: true,
  showRotate: true,
  showZoom: true,
  showFullscreen: true,
  showTheme: false,
  enableKeyboardNavigation: true,
  enableContextMenu: false,
  workerSrc: 'assets/pdfjs/pdf.worker.min.mjs',
  maxRenderedPages: 12,
  maxDevicePixelRatio: 2,
};

/** DI token holding the resolved {@link PdfViewerConfig}. Optional — the component falls back to defaults. */
export const PDF_VIEWER_CONFIG = new InjectionToken<PdfViewerConfig>('PDF_VIEWER_CONFIG');

/**
 * Register global PDF-viewer defaults at the application (or route) level.
 *
 * ```ts
 * // app.config.ts
 * providers: [
 *   providePdfViewer({ defaultZoom: 100, maxZoom: 500, theme: 'light' }),
 * ];
 * ```
 */
export function providePdfViewer(config: Partial<PdfViewerConfig> = {}): EnvironmentProviders {
  return makeEnvironmentProviders([
    { provide: PDF_VIEWER_CONFIG, useValue: { ...DEFAULT_PDF_VIEWER_CONFIG, ...config } },
  ]);
}
